import { useAuth } from "../lib/auth";
import { isPlatformDeveloper } from "../lib/permissions";

// Repo path used to build the promote-to-production link. Kept as a
// constant instead of an env var because it's a public repo URL —
// nothing sensitive, and hardcoding avoids yet another VITE_* to
// forget setting.
const REPO = "bond240/ShiftManager";
const STAGING_BRANCH = "claude/law-enforcement-scheduler-cWezn";

// Floating banner for the platform developer, on BOTH channels, so
// I can jump between preview and production during testing without
// hunting for the URL. Non-developers never see anything —
// isPlatformDeveloper() is the sole gate.
//
//   * On PREVIEW  → amber "Preview build" chip with a "Promote to
//                   production" button (opens a pre-filled PR on
//                   GitHub) and a "Live site" link back to prod.
//                   The link carries ?stable=1 so the prod auto-
//                   hop doesn't immediately fling us back here.
//   * On STABLE   → sky "Live production build" chip with a
//                   "Switch to preview" button. The link goes to
//                   /preview/ plainly — auto-hop is already on
//                   ?stable, but the developer clicked deliberately.
//
// Both variants show the current build SHA so it's obvious which
// bundle is running at any time.
export function StagingBanner() {
  const me = useAuth((s) => s.user);
  if (!isPlatformDeveloper(me)) return null;

  if (__BUILD_CHANNEL__ === "preview") {
    const promoteUrl =
      `https://github.com/${REPO}/compare/main...${STAGING_BRANCH}`
      + `?quick_pull=1`
      + `&title=${encodeURIComponent("Promote preview to production")}`
      + `&body=${encodeURIComponent(
          `Promoting the preview build to production.\n\nBuild: ${__BUILD_SHA__} at ${__BUILD_AT__}`,
        )}`;
    return (
      <div
        className="fixed top-3 right-3 z-[100] flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 shadow-lg px-3 py-2 text-xs print:hidden"
        role="status"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
        <div className="flex flex-col leading-tight">
          <span className="font-semibold text-amber-900">Preview build</span>
          <span className="text-amber-800 font-mono">
            {__BUILD_SHA__} · staging
          </span>
        </div>
        <a
          href={promoteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 rounded bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 font-semibold"
        >
          Promote to production →
        </a>
        <a
          href="https://pdshiftmanager.com/?stable=1"
          className="rounded border border-amber-400 text-amber-900 hover:bg-amber-100 px-2 py-1"
          title="Switch to the production build (won't auto-hop back this session)"
        >
          Live site
        </a>
      </div>
    );
  }

  // Stable channel — symmetric affordance for the developer to jump
  // to the preview build. Clears the "smStayOnStable" session flag
  // as the link is clicked so the auto-hop can resume normally
  // afterward if the developer bounces back.
  return (
    <div
      className="fixed top-3 right-3 z-[100] flex items-center gap-2 rounded-lg border border-sky-300 bg-sky-50 shadow-lg px-3 py-2 text-xs print:hidden"
      role="status"
    >
      <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
      <div className="flex flex-col leading-tight">
        <span className="font-semibold text-sky-900">Live production build</span>
        <span className="text-sky-800 font-mono">
          {__BUILD_SHA__} · stable
        </span>
      </div>
      <a
        href="https://pdshiftmanager.com/preview/"
        onClick={() => {
          try { sessionStorage.removeItem("smStayOnStable"); } catch { /* private mode */ }
        }}
        className="ml-2 rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 font-semibold"
        title="Open the preview build for testing"
      >
        Switch to preview →
      </a>
    </div>
  );
}
